import { z } from 'zod';

/**
 * Invoice Generation Status
 * Tracks the lifecycle of PDF generation for subscription invoices
 */
export const InvoiceGenerationStatus = z.enum(['GENERATING', 'COMPLETED', 'FAILED']);
export type InvoiceGenerationStatus = z.infer<typeof InvoiceGenerationStatus>;

/**
 * Tax Invoice Filter Schema
 * Query parameters for GET /vendors/me/tax-invoices endpoint
 */
export const TaxInvoiceFilterSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
  status: InvoiceGenerationStatus.optional(),
  planName: z.string().optional(),
});
export type TaxInvoiceFilters = z.infer<typeof TaxInvoiceFilterSchema>;

/**
 * Tax Invoice Response Schema
 * Single invoice item in the response
 */
export const TaxInvoiceResponseSchema = z.object({
  id: z.string().uuid(),
  invoiceNumber: z.string(),
  dateIssued: z.string(), // ISO date string
  billingPeriod: z.string(),
  planName: z.string(),
  amount: z.number(), // Base amount before GST
  cgstAmount: z.number(),
  sgstAmount: z.number(),
  igstAmount: z.number(),
  gstAmount: z.number(),
  totalAmount: z.number(),
  status: InvoiceGenerationStatus,
  pdfUrl: z.string().nullable(),
});
export type TaxInvoiceResponse = z.infer<typeof TaxInvoiceResponseSchema>;

/**
 * Tax Invoice List Response Schema
 * Paginated response for invoice list endpoint
 */
export const TaxInvoiceListResponseSchema = z.object({
  items: z.array(TaxInvoiceResponseSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type TaxInvoiceListResponse = z.infer<typeof TaxInvoiceListResponseSchema>;

/**
 * GST Totals Schema
 * Server-side aggregated GST totals across the full filtered dataset
 */
export const GstTotalsSchema = z.object({
  base: z.number(),
  cgst: z.number(),
  sgst: z.number(),
  igst: z.number(),
  total: z.number(),
});
export type GstTotals = z.infer<typeof GstTotalsSchema>;

/**
 * Admin Tax Invoice Filter Schema
 * Extends vendor filters with admin-specific fields (vendorId, search)
 */
export const AdminTaxInvoiceFilterSchema = TaxInvoiceFilterSchema.extend({
  vendorId: z.string().uuid().optional(),
  search: z.string().optional(),
});
export type AdminTaxInvoiceFilters = z.infer<typeof AdminTaxInvoiceFilterSchema>;

/**
 * Admin Tax Invoice Response Schema
 * Extends vendor response with vendor identification fields
 */
export const AdminTaxInvoiceResponseSchema = TaxInvoiceResponseSchema.extend({
  vendorId: z.string().uuid(),
  vendorName: z.string(),
});
export type AdminTaxInvoiceResponse = z.infer<typeof AdminTaxInvoiceResponseSchema>;

/**
 * Admin Tax Invoice List Response Schema
 * Paginated response with server-side GST totals
 */
export const AdminTaxInvoiceListResponseSchema = z.object({
  items: z.array(AdminTaxInvoiceResponseSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
  totals: GstTotalsSchema.optional(),
});
export type AdminTaxInvoiceListResponse = z.infer<typeof AdminTaxInvoiceListResponseSchema>;
